import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSelectChange } from '@angular/material';
import * as moment from 'moment-timezone';
import { FormGroupWrapper } from '../../lead-generation/shared/domain/FormGroupWrapper';
import { IvrMenuEntry, IvrModel } from '../ivr.model';
import { defaultDays, defaultHours } from '../../lead-generation/shared/domain/WorkingHours';
import { IvrService } from '../ivr.service';
import { NotificationService } from '../../shared/notification.service';
import { AmplitudeService } from '../../shared/services/amplitude.service';
import { Ng1ModalService } from '../../shared/ng1/ng1-modal.service';

@Component({
  selector: 'cc-ivr-details',
  templateUrl: './ivr-details.component.html',
  styleUrls: ['./ivr-details.component.scss']
})
export class IvrDetailsComponent implements OnInit {
  public ivrForm: FormGroup;
  public workingHoursFormWrapper: FormGroupWrapper = { form: null };

  public allAvailableMsisdns = [];
  public assignedMsisdns = [];

  public ivr: IvrModel;
  public isCreatingNewIvr: boolean;

  public greetingFileId: string;
  public redirectUrl: string;
  public msisdnId: string;

  public temporaryFileName: string;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private ivrService: IvrService,
    private router: Router,
    private notificationService: NotificationService,
    private amplitudeService: AmplitudeService,
    private ng1ModalService: Ng1ModalService
  ) {}

  ngOnInit() {
    this.assignedMsisdns = this.route.snapshot.data['assignedMsisdns'] || [];
    this.allAvailableMsisdns = this.route.snapshot.data['msisdns'];
    this.isCreatingNewIvr = !this.route.snapshot.data['ivr'];
    this.redirectUrl = this.route.snapshot.queryParams['redirectTo'];
    this.msisdnId = this.route.snapshot.queryParams['msisdnId'];

    if (this.isCreatingNewIvr) {
      this.initEmptyForm();
    } else {
      this.initFormFromData(this.ivr);
    }

    this.handleWorkingHoursAmplitude();
  }

  private handleWorkingHoursAmplitude() {
    // timeout is required, because workingHoursFormWrapper.form is initialized in child's onInit
    setTimeout(() => {
      this.workingHoursFormWrapper.form.get('enabled').valueChanges.subscribe(value => {
        this.amplitudeService.logEvent(`Panel:Ivr:Details: Toggle working hours`, { on: value });
      });
    }, 0);
  }

  initEmptyForm() {
    this.ivr = this.route.snapshot.data['ivr'] || {
      name: ['', Validators.required],
      msisdnWelcomeMessageEnabled: [''],
      workingHours: {
        enabled: false,
        ranges: [{ hours: [defaultHours], days: defaultDays }],
        timezone: moment.tz.guess()
      },
      menus: [
        { greetingFileId: ['', Validators.required], firstLevelMenu: true, menuEntries: [[], Validators.required] }
      ]
    };
    this.ivrForm = this.formBuilder.group(
      {
        name: ['', Validators.required],
        msisdnWelcomeMessageEnabled: [''],
        menus: this.formBuilder.array([
          this.formBuilder.group({
            id: [],
            greetingFileName: [],
            greetingFileId: ['', Validators.required],
            firstLevelMenu: [true],
            greetingIterationsCount: [1, [Validators.required, Validators.min(0)]],
            interGreetingPauseSecs: [10, [Validators.required, Validators.min(0)]],
            menuEntries: this.formBuilder.array([], Validators.required)
          })
        ])
      },
      { validator: this.atLeastOneFileValidator }
    );
  }

  initFormFromData(ivr: IvrModel) {
    this.ivr = this.route.snapshot.data['ivr'];
    this.ivrForm = this.formBuilder.group(
      {
        id: [this.ivr.id, Validators.required],
        name: [this.ivr.name, Validators.required],
        msisdnWelcomeMessageEnabled: [this.ivr.msisdnWelcomeMessageEnabled],
        menus: this.formBuilder.array([
          this.formBuilder.group({
            id: [this.ivr.menus[0].id],
            greetingFileName: [this.ivr.menus[0].greetingFileName],
            greetingFileId: [],
            firstLevelMenu: [true],
            greetingIterationsCount: [
              this.ivr.menus[0].greetingIterationsCount,
              [Validators.required, Validators.min(0)]
            ],
            interGreetingPauseSecs: [
              this.ivr.menus[0].interGreetingPauseSecs,
              [Validators.required, Validators.min(0)]
            ],
            menuEntries: this.formBuilder.array(
              this.ivr.menus[0].menuEntries.map(entry => this.createIvrItemFromData(entry)),
              Validators.required
            )
          })
        ])
      },
      { validator: this.atLeastOneFileValidator }
    );
  }

  get selectedKeys() {
    return new Set(this.ivrMenuEntries.value.map(ivr => ivr.digit));
  }

  get ivrMenuEntries() {
    return this.ivrForm.get('menus.0').get('menuEntries') as FormArray;
  }

  get ivrMenu() {
    return this.ivrForm.get('menus.0');
  }

  get greetingFileName() {
    return this.ivr.menus[0].greetingFileName;
  }

  set greetingFileName(fileName) {
    this.ivr.menus[0].greetingFileName = fileName;
  }

  atLeastOneFileValidator = formGroup => {
    return (this.isCreatingNewIvr
    ? formGroup.get('menus.0').get('greetingFileId').value
    : formGroup.get('menus.0').get('greetingFileName').value || formGroup.get('menus.0').get('greetingFileId').value)
      ? null
      : { fileRequired: true };
  };

  onSelectPhoneNumber(select: MatSelectChange) {
    const selectedMsisdn = select.value;

    if (!selectedMsisdn) {
      return;
    }

    this.assignedMsisdns.push(selectedMsisdn);
  }

  createIvrItem(key): FormGroup {
    this.ivrForm.get('menus.0.menuEntries').markAsTouched();
    return this.formBuilder.group({
      digit: key,
      action: ['SELECT_INBOUND', Validators.required],
      label: ['', Validators.required],
      msisdn: ['', Validators.required]
    });
  }

  createIvrItemFromData(ivrMenuItem: IvrMenuEntry): FormGroup {
    return this.formBuilder.group({
      digit: ivrMenuItem.digit,
      action: [ivrMenuItem.action, Validators.required],
      label: [ivrMenuItem.label, Validators.required],
      msisdn: [ivrMenuItem.msisdn, Validators.required]
    });
  }

  removeIvrItem(index) {
    this.amplitudeService.logEvent(`Panel:Ivr:Details: Remove ivr item`);
    this.ivrMenuEntries.removeAt(index);
  }

  onCharacterClicked(key: string) {
    if (!this.selectedKeys.has(key)) {
      this.amplitudeService.logEvent(`Panel:Ivr:Details: Key clicked`, { key });
      this.selectedKeys.add(key);
      const items = this.ivrMenuEntries;
      items.push(this.createIvrItem(key));
    }
  }

  mapFormValuesToConfig() {
    return {
      ...this.ivrForm.getRawValue(),
      workingHours: this.workingHoursFormWrapper.form.getRawValue(),
      assignedMsisdns: this.assignedMsisdns
    };
  }

  get isFormValid() {
    return (
      this.ivrForm.valid &&
      (!this.workingHoursFormWrapper.form ||
        this.workingHoursFormWrapper.form.valid ||
        !this.workingHoursFormWrapper.form.get('enabled').value)
    );
  }

  onFileUploaded({ fileItem, response }) {
    this.amplitudeService.logEvent('Panel:Ivr:Details: File uploaded');
    this.greetingFileId = response;
    this.temporaryFileName = fileItem && fileItem.file.name;

    this.ivrForm
      .get('menus.0')
      .get('greetingFileId')
      .setValue(response);
  }

  onFileRemove() {
    this.amplitudeService.logEvent('Panel:Ivr:Details: File removed');

    this.greetingFileId = '';
    this.greetingFileName = '';
    this.temporaryFileName = '';

    this.ivrForm
      .get('menus.0')
      .get('greetingFileId')
      .setValue('');

    this.ivrForm
      .get('menus.0')
      .get('greetingFileId')
      .markAsTouched();

    this.ivrForm
      .get('menus.0')
      .get('greetingFileName')
      .setValue('');

    this.ivrForm
      .get('menus.0')
      .get('greetingFileName')
      .markAsTouched();
  }

  markFormGroupTouched(formGroup: FormGroup) {
    (<any>Object).values(formGroup.controls).forEach(control => {
      control.markAsTouched();

      if (control.controls) {
        this.markFormGroupTouched(control);
      }
    });
  }

  get shouldShowFileError() {
    return (
      (this.ivrForm.get('menus.0').get('greetingFileId').touched ||
        this.ivrForm.get('menus.0').get('greetingFileName').touched) &&
      this.ivrForm.hasError('fileRequired')
    );
  }

  get shouldShowKeypadError() {
    return (
      this.ivrForm.get('menus.0.menuEntries').touched && this.ivrForm.get('menus.0.menuEntries').hasError('required')
    );
  }

  onSubmit() {
    if (!this.isFormValid) {
      this.amplitudeService.logEvent(
        `Panel:Ivr:Details: ${this.isCreatingNewIvr ? 'add' : 'update'} submitted - validation failed`
      );
      this.markFormGroupTouched(this.ivrForm);
      this.markFormGroupTouched(this.ivrForm.get('menus.0') as FormGroup);
      return;
    }
    this.amplitudeService.logEvent(`Panel:Ivr:Details: ${this.isCreatingNewIvr ? 'add' : 'update'} submitted`);

    if (this.isCreatingNewIvr) {
      this.createNewIvr();
    } else {
      this.updateExistingIvr();
    }
  }

  private updateExistingIvr() {
    this.ivrService.updateIvr(`${this.ivr.id}`, this.mapFormValuesToConfig()).subscribe(
      () => {
        this.amplitudeService.logEvent(`Panel:Ivr:Details: Successfully updated`);
        this.notificationService.open('IVR.SUCCESS_UPDATE_IVR');
        this.router.navigate(['/ivr']);
      },
      () => {
        this.amplitudeService.logEvent(`Panel:Ivr:Details: ${this.isCreatingNewIvr ? 'add' : 'update'} error`);
      }
    );
  }

  private async createNewIvr() {
    const limitsExceeded = await this.ivrService.checkIfLimitsExceeded().toPromise();
    if (limitsExceeded) {
      this.amplitudeService.logEvent(`Panel:Ivr:Details: Cannot create - account limits exceeded`);
      this.ng1ModalService.lazyModalService.showUpgradeAccountModal();
      return;
    }

    this.ivrService.createIvr(this.mapFormValuesToConfig()).subscribe(
      ivr => {
        this.notificationService.open('IVR.SUCCESS_ADD_IVR');
        this.amplitudeService.logEvent(`Panel:Ivr:Details: Successfully added`);
        if (this.redirectUrl) {
          this.ivrService.assignIvrToNumber(this.msisdnId, `${ivr.id}`).subscribe(() => {
            this.router.navigateByUrl(this.redirectUrl);
          });
        } else {
          this.router.navigate(['/ivr']);
        }
      },
      () => {
        this.amplitudeService.logEvent(`Panel:Ivr:Details: ${this.isCreatingNewIvr ? 'add' : 'update'} error`);
      }
    );
  }
}
